import { type IntlShape } from 'react-intl';
import { Campaign } from 'types';

// these need to match up with declaration in models.Campaign

export const INTRODUCTION_UI_PHASE_KEY = 'introduction_ui_phase';
// Possible performance phases
export const PHASE_TYPE_INTRODUCTION_UI_ONLY = 'INTRO';
export const PHASE_TYPE_SELF = 'S';
export const PHASE_TYPE_OTHERS = 'O';
export const PHASE_TYPE_EVALUATION = 'E';
export const PHASE_TYPE_CALIBRATION = 'C';
export const PHASE_TYPE_REPORTING = 'R';
export const PHASE_NAMES = {
  [PHASE_TYPE_SELF]: 'self',
  [PHASE_TYPE_OTHERS]: 'others',
  [PHASE_TYPE_EVALUATION]: 'evaluation',
  [PHASE_TYPE_CALIBRATION]: 'calibration',
  [PHASE_TYPE_REPORTING]: 'reporting',
};
export const PHASE_TYPES_IN_ORDER = [
  PHASE_TYPE_SELF,
  PHASE_TYPE_OTHERS,
  PHASE_TYPE_EVALUATION,
  PHASE_TYPE_CALIBRATION,
  PHASE_TYPE_REPORTING,
];
// list of performance features enabled in a given phase
// NOTE: these should match the phase objects declared in a given phase object
// in the backend campaign object's phase array
// And any changes here should also be changed in models.py's Campaign object
// in the backend to match
// **IMPORTANT** FOR ANY NEW FEATURES HERE, BE SURE TO ADD THEM TO THE CAMPAIGN
// EDITOR SO ADMINS AT ORGANIZATIONS CAN CONFIGURE THEM BY THEMSELVES

export const PERFORMANCE_FEATURE_WELCOME_HEADER = 'welcome_header';
export const PERFORMANCE_FEATURE_WELCOME_MEDIA = 'welcome_media';
export const PERFORMANCE_FEATURE_WELCOME_MEDIA_CAPTION =
  'welcome_media_caption';
export const PERFORMANCE_FEATURE_WELCOME_MESSAGE_HTML = 'welcome_message_html';
export const PERFORMANCE_FEATURE_EVALUATION_PHASE_OPEN_RESPONSES_HELPER_TEXT =
  'open_responses_helper_text';
export const PERFORMANCE_FEATURE_NETWORK_STAKEHOLDERS = 'network_stakeholders';
export const PERFORMANCE_FEATURE_PREVIOUS_DIRECT_REPORT_CALLOUTS =
  'previous_direct_report_callouts';
export const PERFORMANCE_FEATURE_PREVIOUS_DIRECT_REPORT_COMMENTS_REQUIRED =
  'previous_direct_report_comments_required';
export const PERFORMANCE_FEATURE_GOLD_STAR_COMMENTS_REQUIRED =
  'gold_star_comments_required';
export const PERFORMANCE_FEATURE_HEADS_UP_COMMENTS_REQUIRED =
  'heads_up_comments_required';
export const PERFORMANCE_FEATURE_SELF_REFLECTION_INTRO_MESSAGE_HTML =
  'self_reflection_intro_message_html';
export const DEFAULT_SELF_REFLECTION_INTRO_MESSAGE_HTML = (formatMessage) =>
  formatMessage({
    id: 'app.utils.models.performance.self_reflection_intro_message_html.default',
    defaultMessage:
      '<p>The last few steps were about the people around you. ' +
      'Next, we&apos;re going to switch gears and talk about you.</p>' +
      '<p>On the next page, you&apos;ll be asked questions about yourself. ' +
      'Your manager will be able to see your responses.</p>',
  });
export const PERFORMANCE_FEATURE_COMPLETE_RESUME_STEP = 'complete_resume_step';
export const PERFORMANCE_FEATURE_COMPLETE_RESUME_STEP_AI_DRAFT =
  'complete_resume_step_ai_draft';
export const PERFORMANCE_FEATURE_COMPLETE_OBJECTIVES_STEP =
  'complete_objectives_step'; // Deprecated
export const PERFORMANCE_FEATURE_COMPLETE_OBJECTIVES_FOR_QUARTER_STEP =
  'complete_objectives_for_quarter_step';
export const PERFORMANCE_FEATURE_OBJECTIVES_HEADER = 'objectives_header';
export const DEFAULT_OBJECTIVES_HEADER = (
  formatMessage: IntlShape['formatMessage']
) =>
  formatMessage({
    id: 'app.utils.models.performance.objectives_header.default',
    defaultMessage: 'Define your objectives.',
  });
export const DEFAULT_OBJECTIVES_MESSAGE_HTML = (
  formatMessage: IntlShape['formatMessage']
) =>
  formatMessage({
    id: 'app.utils.models.performance.objectives_message_html.default',
    defaultMessage:
      'Consider the work you plan to do for the next quarter. Set your objectives and key results you would like to obtain accordingly.',
  });
export const PERFORMANCE_FEATURE_OBJECTIVES_MESSAGE_HTML =
  'objectives_message_html';
export const PERFORMANCE_FEATURE_CONTRIBUTION_SELF_REFLECTIONS =
  'contribution_self_reflections';
export const PERFORMANCE_FEATURE_SELF_OPEN_RESPONSE_QUESTIONS =
  'self_open_response_questions';
export const PERFORMANCE_FEATURE_OTHERS_OPEN_RESPONSE_QUESTIONS =
  'others_open_response_questions';
export const PERFORMANCE_FEATURE_EVALUATION_OPEN_RESPONSE_QUESTIONS =
  'evaluation_open_response_questions';
export const PERFORMANCE_FEATURE_ASSESS_ORGANIZATION_OPEN_RESPONSE_QUESTIONS =
  'assess_organization_open_response_questions';
export const PERFORMANCE_FEATURE_ENGAGEMENT_SURVEY_FILTERS =
  'engagement_survey_filters';
export const PERFORMANCE_FEATURE_ENGAGEMENT_SURVEY_SUMMARIES =
  'engagement_survey_summaries';
export const PERFORMANCE_FEATURE_HIGH_PRIORITY_PEERS = 'high_priority_peers';
export const PERFORMANCE_FEATURE_ASSESS_MANAGER = 'assess_manager';
export const PERFORMANCE_FEATURE_ASSESS_MANAGER_ATTRIBUTION =
  'assess_manager_attribution';
export const PERFORMANCE_FEATURE_PEER_FEEDBACK_ATTRIBUTION =
  'peer_feedback_attribution';
export const PERFORMANCE_FEATURE_PEER_FEEDBACK = 'peer_feedback';
export const PERFORMANCE_FEATURE_PEER_FEEDBACK_ASSIGNMENT_TIME =
  'peer_feedback_assignment_time';
export const PERFORMANCE_FEATURE_PEER_FEEDBACK_ASSIGNMENT_DONE =
  'peer_feedback_assignment_done';
export const PERFORMANCE_FEATURE_ADD_OPTIONAL_PEER_FEEDBACK = 'optional_peers';
export const PERFORMANCE_FEATURE_ASSESS_ORGANIZATION = 'assess_organization';
export const PERFORMANCE_FEATURE_EMPLOYEE_NPS_QUESTION_NAME =
  'custom_question_employee_nps_rating';
export const PERFORMANCE_FEATURE_EMPLOYEE_NPS_QUESTION_ENABLED =
  'employee_nps_question_enabled';
export const PERFORMANCE_FEATURE_EMPLOYEE_NPS_QUESTION_OPTIONAL =
  'employee_nps_question_optional';
export const PERFORMANCE_FEATURE_HIDE_PEER_FEEDBACK_FROM_SUBJECT =
  'hide_peer_feedback_from_subject';
export const PERFORMANCE_FEATURE_HIDE_ASSESS_MANAGER_FEEDBACK_FROM_SUBJECT =
  'hide_assess_manager_feedback_from_subject';
export const PERFORMANCE_FEATURE_HIDE_ADDITIONAL_MANAGER_FEEDBACK_FROM_SUBJECT =
  'hide_additional_manager_feedback_from_subject';
export const PERFORMANCE_FEATURE_PROMOTION_NOMINATION_BY_MANAGER =
  'promotion_nomination_by_manager';
export const PERFORMANCE_FEATURE_PROMOTION_NOMINATION_BY_MANAGER_ALLOW_CALIBRATION =
  'promotion_nomination_by_manager_allow_calibration';
export const PERFORMANCE_FEATURE_PROMOTION_NOMINATION_BY_MANAGER_LABEL =
  'promotion_nomination_by_manager_label';
export const PERFORMANCE_FEATURE_PROMOTION_NOMINATION_BY_MANAGER_HELPER_TEXT =
  'promotion_nomination_by_manager_helper_text';
export const PERFORMANCE_FEATURE_PROMOTION_NOMINATION_BY_MANAGER_ALLOW_COMMENTS =
  'promotion_nomination_by_manager_allow_comments';
export const PERFORMANCE_FEATURE_PROMOTION_PACKETS_REQUIRED =
  'promotion_packets_required';
export const PERFORMANCE_FEATURE_SALARY_INCREASE_NOMINATION_BY_MANAGER =
  'SALARY_INCREASE_NOMINATION_by_manager';
export const PERFORMANCE_FEATURE_SALARY_INCREASE_NOMINATION_BY_MANAGER_LABEL =
  'SALARY_INCREASE_NOMINATION_by_manager_label';
export const PERFORMANCE_FEATURE_SALARY_INCREASE_NOMINATION_BY_MANAGER_HELPER_TEXT =
  'SALARY_INCREASE_NOMINATION_by_manager_helper_text';
export const PERFORMANCE_FEATURE_SALARY_INCREASE_NOMINATION_BY_MANAGER_ALLOW_COMMENTS =
  'SALARY_INCREASE_NOMINATION_by_manager_allow_comments';
export const PERFORMANCE_FEATURE_MANAGER_CONTRIBUTION_FEEDBACK =
  'manager_contribution_feedback';
export const PERFORMANCE_FEATURE_PEER_CONTRIBUTION_FEEDBACK =
  'peer_contribution_feedback';
export const PERFORMANCE_FEATURE_HIDE_INFLUENCE_FROM_MANAGERS =
  'hide_influence_from_managers';
export const PERFORMANCE_FEATURE_SHOW_RECOGNITION_TO_MANAGERS =
  'show_recognition_to_managers';
export const PERFORMANCE_FEATURE_SHOW_OBJECTIVES_TO_MANAGERS =
  'show_objectives_to_managers';
export const PERFORMANCE_FEATURE_HIDE_TIMELINE_ACTIVITIES =
  'hide_timeline_activities';
export const PERFORMANCE_FEATURE_SHOW_CRITICAL_FEEDBACK_TO_MANAGERS =
  'show_critical_feedback_to_managers';
export const PERFORMANCE_FEATURE_HIDE_EVALUATION_POSITIVE_TAG_RECOMMENDATIONS =
  'hide_evaluation_positive_tag_recommendations';
export const PERFORMANCE_FEATURE_HIDE_EVALUATION_NEGATIVE_TAG_RECOMMENDATIONS =
  'hide_evaluation_negative_tag_recommendations';
export const PERFORMANCE_FEATURE_SHOW_TALENT_MATRICES = 'show_talent_matrices';
export const PERFORMANCE_FEATURE_TALENT_MATRICES = 'talent_matrices';
export const PERFORMANCE_FEATURE_ALLOW_FEEDBACK_REQUESTS_WHEN_CLAIMING_CONTRIBUTIONS =
  'allow_feedback_requests_when_claiming_contributions';
export const PERFORMANCE_FEATURE_HIDE_TIME_AT_ORGANIZATION =
  'hide_time_at_organization';
export const PERFORMANCE_FEATURE_HIDE_TIME_IN_ROLE = 'hide_time_in_role';
export const PERFORMANCE_FEATURE_RATING_SCALE = 'rating_scale';
export const PERFORMANCE_FEATURE_HEADS_UP_ANONYMOUS_TOGGLE =
  'heads_ups_anonymous_toggle';
export const PERFORMANCE_FEATURE_DISPLAY_HEADS_UPS_TO_RECIPIENT =
  'display_heads_ups_to_recipient';
export const PERFORMANCE_FEATURE_GOLD_STARS_AND_INFLUENCE_VISIBLE_TO_RECIPIENT =
  'gold_stars_and_influence_visible_to_recipient';
// Used only in cases where "gold_stars_and_influence_visible_to_recipient" is enabled
// BUT we want to hide the influence section (so only show gold stars, ie. Carta H2 2022)
export const PERFORMANCE_FEATURE_HIDE_INFLUENCE_FROM_RECIPIENT =
  'hide_influence_from_recipient';
export const PERFORMANCE_FEATURE_SHOW_PERFORMANCE_PERIOD_WITH_SPECIFIC_DATES =
  'show_performance_period_with_specific_dates';
export const PERFORMANCE_FEATURE_MANAGER_UNEDITABLE = 'manager_uneditable';
export const PERFORMANCE_FEATURE_INCLUDE_BASICS_PAGE = 'include_basics_page';
export const PERFORMANCE_FEATURE_INCLUDE_OPENING_CHECK_IN =
  'include_opening_check_in';
export const PERFORMANCE_FEATURE_MINIMUM_MANDATORY_PEERS =
  'minimum_mandatory_peers';
export const PERFORMANCE_FEATURE_MAXIMUM_MANDATORY_PEERS =
  'maximum_mandatory_peers';
export const PERFORMANCE_FEATURE_UPWARD_MANAGER_OPEN_RESPONSE_QUESTIONS =
  'upward_manager_open_response_questions';
export const PERFORMANCE_FEATURE_UPWARD_MANAGER_OPEN_RESPONSE_QUESTIONS_OUTGOING =
  PERFORMANCE_FEATURE_UPWARD_MANAGER_OPEN_RESPONSE_QUESTIONS + '_outgoing';
export const PERFORMANCE_FEATURE_RESUME_OKR_REMINDER = 'resume_okr_reminder';
export const PERFORMANCE_FEATURE_HIDE_PULSE_ANONYMOUS_DESCRIPTION =
  'hide_pulse_survey_anonymous_description';
export const PERFORMANCE_FEATURE_ENGAGEMENT_SURVEY_ANONYMOUS_FILTER =
  'engagement_survey_anonymous_filter';
export const PERFORMANCE_FEATURE_HIDE_MANAGER_RATING = 'hide_manager_rating';
export const PERFORMANCE_FEATURE_HIDE_RATING_COMMENTS = 'hide_rating_comments';
export const PERFORMANCE_FEATURE_MANAGER_RATING_IS_INTERIM_RATING =
  'manager_rating_is_interim_rating';
export const PERFORMANCE_FEATURE_HIDE_RATING_FROM_DIRECT_REPORT =
  'hide_rating_from_direct_report';
export const PERFORMANCE_FEATURE_SHOW_RATING_COMMENTS_TO_DIRECT_REPORT =
  'show_rating_comments_to_direct_report';
export const PERFORMANCE_FEATURE_RATINGS_HISTORY_NUMBER_OF_PAST_CYCLES =
  'ratings_history_count_number_of_past_cycles';
export const DEFAULT_PERFORMANCE_FEATURE_RATINGS_HISTORY_NUMBER_OF_PAST_CYCLES = 1;
export const PERFORMANCE_FEATURE_ALLOW_REPORT_RELEASE_DURING_EVALUATION =
  'allow_report_release_during_evaluation';
export const PERFORMANCE_CALLOUTS_DESCRIPTION = 'callouts_description';
export const DEFAULT_PERFORMANCE_CALLOUTS_DESCRIPTION = (formatMessage) =>
  formatMessage({
    id: 'app.utils.models.performance.callouts_description.default',
    defaultMessage: 'Tell us about your Callouts.',
  });
export const PERFORMANCE_NETWORK_DESCRIPTION = 'network_description';
export const DEFAULT_PERFORMANCE_NETWORK_DESCRIPTION = (formatMessage) =>
  formatMessage({
    id: 'app.utils.models.performance.network_description.default',
    defaultMessage: 'Optional: What makes your coworkers great?',
  });
export const PERFORMANCE_FEATURE_HIDE_PHASE_DEADLINE = 'hide_phase_deadline';
export const PERFORMANCE_PHASE_DEADLINE_TEXT = 'phase_deadline_text';
// replaced with the phase end date wherever the phase deadline text is shown
export const PHASE_DEADLINE_DATE_PLACEHOLDER = '{{date}}';
export const DEFAULT_PERFORMANCE_PHASE_DEADLINE_TEXT = (formatMessage) =>
  formatMessage(
    {
      id: 'app.utils.models.performance.phase_deadline_text.default',
      defaultMessage:
        'You will have until {date} to complete your responses. You can revise them any time until then.',
    },
    { date: PHASE_DEADLINE_DATE_PLACEHOLDER }
  );
export const DEFAULT_PERFORMANCE_PROMOTION_NOMINATION_BY_MANAGER_LABEL =
  undefined;
export const DEFAULT_PERFORMANCE_PROMOTION_NOMINATION_BY_MANAGER_HELPER_TEXT =
  undefined;
export const DEFAULT_PERFORMANCE_SALARY_INCREASE_NOMINATION_BY_MANAGER_LABEL =
  undefined;
export const DEFAULT_PERFORMANCE_SALARY_INCREASE_NOMINATION_BY_MANAGER_HELPER_TEXT =
  undefined;
export const PERFORMANCE_NETWORK_STEP_PEOPLE_MINIMUM = 'network_people_minimum';
export const DEFAULT_INFLUENCE_PEOPLE_MINIMUM = 3;
export const DEFAULT_MINIMUM_SURVEY_RESPONDENTS = 3;
export const PERFORMANCE_ACCOMPLISHMENTS_DEFAULT_VISIBILITY_PRIVATE =
  'accomplishments_default_visibility_private';
export const PERFORMANCE_ACCOMPLISHMENTS_MINIMUM = 'accomplishments_minimum';
export const PERFORMANCE_ACCOMPLISHMENTS_MAXIMUM = 'accomplishments_maximum';
export const PERFORMANCE_ACCOMPLISHMENTS_INSTRUCTIONS = 'resume_instructions';
export const PERFORMANCE_FEATURE_DISABLE_AUTO_CALIBRATION =
  'disable_auto_calibration';
export const PERFORMANCE_FEATURE_ALLOW_EXTERNAL_MANAGER_RATINGS =
  'allow_external_manager_ratings';
export const PERFORMANCE_FEATURE_DISABLE_CONVERSATION_ACKNOWLEDGEMENTS =
  'disable_conversation_acknowledgements';
export const PERFORMANCE_FEATURE_DISABLE_NETWORK = 'disable_network';
export const PERFORMANCE_FEATURE_DISABLE_CALLOUTS = 'disable_callouts';
export const PERFORMANCE_FEATURE_MAXIMUM_PEERS_TO_SHOW =
  'maximum_peers_to_show';
export const PERFORMANCE_FEATURE_MINIMUM_SURVEY_RESPONDENTS =
  'minimum_survey_respondents';
export const PERFORMANCE_FEATURE_EVALUATION_AI_ASSISTED_QUESTIONS =
  'evaluation_ai_assisted_questions';
export const PERFORMANCE_FEATURE_SELF_AI_ASSISTED_QUESTIONS =
  'self_ai_assisted_questions';

export const WIZARD_TYPE_PERFORMANCE = 'P';
export const WIZARD_TYPE_ENGAGEMENT_ONLY = 'E';
export const WIZARD_TYPE_ENPS_ONLY = 'N';
export const WIZARD_TYPE_ONA_ONLY = 'O';
export const ENGAGEMENT_ENTITLEMENT_WIZARD_TYPES: string[] = [
  WIZARD_TYPE_ENGAGEMENT_ONLY,
  WIZARD_TYPE_ENPS_ONLY,
];
export const ENGAGEMENT_ENTITLEMENT_PHASE_FEATURES = [
  PERFORMANCE_FEATURE_ASSESS_ORGANIZATION,
  PERFORMANCE_FEATURE_EMPLOYEE_NPS_QUESTION_ENABLED,
] as const;

// copies of the phases, so the source cycle's phases are left untouched
export const switchOffEngagementSurvey = <
  T extends Partial<
    Record<(typeof ENGAGEMENT_ENTITLEMENT_PHASE_FEATURES)[number], unknown>
  >,
>(
  phases: T[]
): T[] =>
  phases.map((phase) => {
    const newPhase = { ...phase };
    for (const feature of ENGAGEMENT_ENTITLEMENT_PHASE_FEATURES) {
      if (newPhase[feature]) {
        newPhase[feature] = false as T[typeof feature];
      }
    }
    return newPhase;
  });

export type WizardTypes =
  | typeof WIZARD_TYPE_PERFORMANCE
  | typeof WIZARD_TYPE_ENGAGEMENT_ONLY
  | typeof WIZARD_TYPE_ENPS_ONLY
  | typeof WIZARD_TYPE_ONA_ONLY;

export const WIZARD_TYPE_KEY = 'wizard_type';

export const isWizardType = (campaign: Campaign, wizardType: string): boolean =>
  getWizardType(campaign) === wizardType;

export const getWizardType = (campaign: Campaign): WizardTypes => {
  const key = campaign?.[WIZARD_TYPE_KEY];
  if (
    key !== WIZARD_TYPE_PERFORMANCE &&
    key !== WIZARD_TYPE_ENGAGEMENT_ONLY &&
    key !== WIZARD_TYPE_ENPS_ONLY &&
    key !== WIZARD_TYPE_ONA_ONLY
  ) {
    console.error('Unrecognized campaign wizard type: ' + key);
    return WIZARD_TYPE_PERFORMANCE;
  }
  return key;
};

export const WIZARD_TYPES_PHASES = {
  [WIZARD_TYPE_ONA_ONLY]: [
    PHASE_TYPE_INTRODUCTION_UI_ONLY,
    PHASE_TYPE_SELF,
    PHASE_TYPE_REPORTING,
  ],
  [WIZARD_TYPE_ENGAGEMENT_ONLY]: [
    PHASE_TYPE_INTRODUCTION_UI_ONLY,
    PHASE_TYPE_OTHERS,
  ],
  [WIZARD_TYPE_ENPS_ONLY]: [PHASE_TYPE_INTRODUCTION_UI_ONLY, PHASE_TYPE_OTHERS],
  [WIZARD_TYPE_PERFORMANCE]: [
    PHASE_TYPE_INTRODUCTION_UI_ONLY,
    PHASE_TYPE_SELF,
    PHASE_TYPE_OTHERS,
    PHASE_TYPE_EVALUATION,
    PHASE_TYPE_CALIBRATION,
    PHASE_TYPE_REPORTING,
  ],
};
export const phaseWithFeatureEnabled = (phase, feature) => {
  return phase[feature];
};

export const getCampaignHasFeatureEnabled = (campaign, feature) => {
  return (
    (campaign?.phases?.length > 0 &&
      campaign.phases.findIndex((p) => phaseWithFeatureEnabled(p, feature)) !==
        -1) ||
    campaign?.configs?.[INTRODUCTION_UI_PHASE_KEY]?.[feature]
  );
};

export const getPhaseDateHasStarted = (startDateString) => {
  // consider the current date matching as started
  return new Date(startDateString) <= new Date();
};

export const getPhaseDateHasPassed = (endDateString) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  // date is midnight on left, but the date end is meant to be after 11:59:59pm,
  // so we need to subtract a date from the current day to account for that
  return new Date(endDateString) < yesterday;
};
export const getPhasePrefix = (index, formatMessage) => {
  if (index === 0)
    return formatMessage({
      id: 'app.utils.models.performance.phase_i',
      defaultMessage: 'Phase I',
    });
  if (index === 1)
    return formatMessage({
      id: 'app.utils.models.performance.phase_ii',
      defaultMessage: 'Phase II',
    });
  if (index === 2)
    return formatMessage({
      id: 'app.utils.models.performance.phase_iii',
      defaultMessage: 'Phase III',
    });
  if (index === 3)
    return formatMessage({
      id: 'app.utils.models.performance.phase_iv',
      defaultMessage: 'Phase IV',
    });
  if (index === 4)
    return formatMessage({
      id: 'app.utils.models.performance.phase_v',
      defaultMessage: 'Phase V',
    });
};
